import React, { useState, useEffect } from 'react'
import axios from 'axios'

const API  = process.env.REACT_APP_API_URL || 'http://localhost:8000'
const TEAL = '#0d9488'
const TEAL_DARK  = '#0f766e'
const TEAL_LIGHT = '#f0fdfa'
const TEAL_MID   = '#99f6e4'
const PURPLE     = '#7c3aed'
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

const lS = { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '5px' }
const iS = { width: '100%', padding: '10px 12px', fontSize: '14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', boxSizing: 'border-box', color: '#1e293b', background: 'white', fontFamily: FONT, outline: 'none' }

// ── Section header ─────────────────────────────────────────────────────────────
function SectionHead({ label, sub }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      {sub && <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '3px' }}>{sub}</div>}
    </div>
  )
}

// ── Card wrapper ───────────────────────────────────────────────────────────────
function Card({ children, style }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.92)', border: '1px solid #e2e8f0',
      borderRadius: '14px', padding: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)', ...style,
    }}>
      {children}
    </div>
  )
}

// ── Add Therapist modal ────────────────────────────────────────────────────────
function AddTherapistModal({ clinicId, onClose, onSuccess }) {
  const [form, setForm]       = useState({ username: '', password: '', confirm: '' })
  const [error, setError]     = useState('')
  const [success, setSuccess] = useState('')
  const [saving, setSaving]   = useState(false)

  const handle = () => {
    setError('')
    if (!form.username || !form.password) { setError('Please fill in all fields.'); return }
    if (form.password !== form.confirm)   { setError('Passwords do not match.'); return }
    if (form.password.length < 8)         { setError('Password must be at least 8 characters.'); return }
    setSaving(true)
    axios.post(API + '/auth/create-user', { username: form.username, password: form.password, role: 'therapist', clinic_id: clinicId })
      .then(() => {
        setSuccess('Account created!')
        setTimeout(() => { onSuccess(); onClose() }, 1500)
      })
      .catch(err => setError(err.response?.data?.detail || 'Failed to create account.'))
      .finally(() => setSaving(false))
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ background: 'white', borderRadius: '14px', padding: '28px', maxWidth: '420px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#0f172a', margin: '0 0 6px' }}>Add Therapist Account</h3>
        <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 20px', lineHeight: '1.5' }}>
          They'll sign in using your clinic code, their username, and this password.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
          <div>
            <label style={lS}>Username</label>
            <input placeholder="e.g. drsmith" value={form.username}
              onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
              style={iS} autoCapitalize="none" autoCorrect="off" />
          </div>
          <div>
            <label style={lS}>Password</label>
            <input type="password" placeholder="At least 8 characters" value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))} style={iS} />
          </div>
          <div>
            <label style={lS}>Confirm Password</label>
            <input type="password" placeholder="Re-enter password" value={form.confirm}
              onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} style={iS} />
          </div>
          {error   && <div style={{ padding: '10px 14px', borderRadius: '8px', fontSize: '13px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>{error}</div>}
          {success && <div style={{ padding: '10px 14px', borderRadius: '8px', fontSize: '13px', background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}>{success}</div>}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: FONT, color: '#334155' }}>Cancel</button>
          <button onClick={handle} disabled={saving} style={{ flex: 2, padding: '11px', background: saving ? '#94a3b8' : TEAL, color: 'white', border: 'none', borderRadius: '8px', cursor: saving ? 'default' : 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: FONT }}>
            {saving ? 'Creating...' : 'Create Account'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────
function Settings({ onSave, clinicId, pendingPriceSelection, currentUser = null }) {
  const [form,           setForm]           = useState({ clinic_name: '', address: '', phone: '', email: '', website: '', location: '' })
  const [therapistInfo,  setTherapistInfo]  = useState(null)
  const [message,        setMessage]        = useState('')
  const [loading,        setLoading]        = useState(true)
  const [showAddModal,   setShowAddModal]   = useState(false)

  useEffect(() => {
    if (!clinicId && currentUser?.username !== 'demo') return
    fetchAll()
  }, [clinicId])

  const fetchAll = () => {
    axios.get(API + '/settings?clinic_id=' + clinicId)
      .then(res => { setForm(res.data); setLoading(false) })
      .catch(() => setLoading(false))
    if (clinicId) {
      axios.get(API + '/auth/clinic/' + clinicId + '/therapists')
        .then(res => setTherapistInfo(res.data))
        .catch(() => {})
    }
  }

  const handleSave = () => {
    axios.put(API + '/settings', { ...form, clinic_id: clinicId })
      .then(() => {
        setMessage('Saved!')
        if (onSave) onSave()
        setTimeout(() => setMessage(''), 3000)
      })
      .catch(() => setMessage('Error saving settings.'))
  }

  if (loading) return <div style={{ color: '#64748b', fontSize: '15px', fontFamily: FONT }}>Loading...</div>

  const plan          = therapistInfo?.plan || 'trial'
  const therapistCount = therapistInfo?.count ?? 0
  const isBeta        = plan === 'trial'

  return (
    <div style={{ maxWidth: '580px', width: '100%', fontFamily: FONT, display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {showAddModal && (
        <AddTherapistModal
          clinicId={clinicId}
          onClose={() => setShowAddModal(false)}
          onSuccess={fetchAll}
        />
      )}

      {/* Page header */}
      <div>
        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>Settings</h2>
        <p style={{ color: '#64748b', fontSize: '14px', marginTop: '6px' }}>Manage your clinic info, team, subscription, and legal documents.</p>
      </div>

      {/* ── 1. Subscription ──────────────────────────────────────────────────── */}
      <Card>
        <SectionHead label="Subscription" sub="Your current plan and billing status." />

        {isBeta ? (
          /* Beta access banner */
          <div style={{ background: `linear-gradient(135deg, ${TEAL_LIGHT}, #f5f3ff)`, border: `1.5px solid ${TEAL_MID}`, borderRadius: '12px', padding: '20px 22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '700', padding: '3px 10px', borderRadius: '99px', background: TEAL, color: 'white' }}>Beta Access</span>
                  <span style={{ fontSize: '13px', fontWeight: '700', padding: '3px 10px', borderRadius: '99px', background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}>Full Access — Free</span>
                </div>
                <p style={{ fontSize: '14px', color: '#334155', margin: 0, lineHeight: '1.6' }}>
                  You have full access to all ClearPath PT features at no charge during the beta period.
                  Billing will begin after beta at <strong>$65 / therapist / month</strong> with all features included.
                </p>
              </div>
            </div>

            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${TEAL_MID}`, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
              {[
                { label: 'Plan', value: 'Beta' },
                { label: 'Therapists', value: `${therapistCount} active` },
                { label: 'Billing starts', value: 'After beta' },
              ].map(item => (
                <div key={item.label}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '3px' }}>{item.label}</div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Active subscription */
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px 22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '700', padding: '3px 10px', borderRadius: '99px', background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', textTransform: 'capitalize' }}>
                    {plan}
                  </span>
                </div>
                <p style={{ fontSize: '14px', color: '#334155', margin: 0 }}>
                  {therapistCount} therapist{therapistCount !== 1 ? 's' : ''} · $65/therapist/month
                </p>
              </div>
              <button
                onClick={() => axios.post(API + '/billing/portal', { clinic_id: clinicId }).then(r => { window.location.href = r.data.url }).catch(() => alert('Unable to open billing portal. Please contact support.'))}
                style={{ padding: '9px 18px', background: 'white', color: '#334155', border: '1.5px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: FONT }}
              >
                Manage Billing →
              </button>
            </div>
          </div>
        )}

        {/* Coming soon pricing note */}
        {isBeta && (
          <div style={{ marginTop: '14px', padding: '12px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', fontSize: '13px', color: '#92400e', lineHeight: '1.6' }}>
            <strong>Upcoming pricing:</strong> $65 / therapist / month — all features included, no per-feature add-ons.
            You'll receive advance notice before billing begins.
          </div>
        )}
      </Card>

      {/* ── 2. Team ───────────────────────────────────────────────────────────── */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
          <SectionHead
            label="Therapist Accounts"
            sub={`${therapistCount} therapist${therapistCount !== 1 ? 's' : ''} on your team.`}
          />
          <button
            onClick={() => setShowAddModal(true)}
            style={{ padding: '7px 14px', background: TEAL, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: FONT, flexShrink: 0 }}
          >
            + Add Therapist
          </button>
        </div>

        {(!therapistInfo?.therapists || therapistInfo.therapists.length === 0) ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px', background: '#f8fafc', borderRadius: '10px' }}>
            No therapist accounts yet. Add one above.
          </div>
        ) : (
          <div style={{ border: '1px solid #f1f5f9', borderRadius: '10px', overflow: 'hidden' }}>
            {therapistInfo.therapists.map((t, i) => (
              <div key={t.id} style={{ padding: '11px 16px', background: i % 2 === 0 ? 'white' : '#fafafa', borderTop: i === 0 ? 'none' : '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: TEAL_LIGHT, border: `1.5px solid ${TEAL_MID}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', color: TEAL, flexShrink: 0 }}>
                  {t.username[0].toUpperCase()}
                </div>
                <span style={{ fontSize: '14px', color: '#0f172a', fontWeight: '500' }}>{t.username}</span>
                <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '99px', background: '#f0fdfa', color: TEAL, border: `1px solid ${TEAL_MID}` }}>Therapist</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── 3. Clinic info ────────────────────────────────────────────────────── */}
      <Card>
        <SectionHead
          label="Clinic Information"
          sub="Appears on the login page when staff verify your clinic code."
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={lS}>Clinic Name</label>
            <input name="clinic_name" value={form.clinic_name || ''} onChange={e => setForm(f => ({ ...f, [e.target.name]: e.target.value }))} placeholder="e.g. River Valley Physical Therapy" style={iS} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={lS}>Phone</label>
              <input name="phone" value={form.phone || ''} onChange={e => setForm(f => ({ ...f, [e.target.name]: e.target.value }))} placeholder="(256) 555-0100" style={iS} />
            </div>
            <div>
              <label style={lS}>Clinic Email</label>
              <input name="email" value={form.email || ''} onChange={e => setForm(f => ({ ...f, [e.target.name]: e.target.value }))} placeholder="info@clinic.com" style={iS} />
            </div>
          </div>
          <div>
            <label style={lS}>Address</label>
            <input name="address" value={form.address || ''} onChange={e => setForm(f => ({ ...f, [e.target.name]: e.target.value }))} placeholder="123 Main St, Huntsville, AL 35801" style={iS} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={lS}>City / Location</label>
              <input name="location" value={form.location || ''} onChange={e => setForm(f => ({ ...f, [e.target.name]: e.target.value }))} placeholder="Huntsville, AL" style={iS} />
            </div>
            <div>
              <label style={lS}>Website</label>
              <input name="website" value={form.website || ''} onChange={e => setForm(f => ({ ...f, [e.target.name]: e.target.value }))} placeholder="www.rivervalleypt.com" style={iS} />
            </div>
          </div>

          {message && (
            <div style={{ padding: '10px 14px', borderRadius: '8px', fontSize: '14px', background: message.includes('Error') ? '#fef2f2' : '#f0fdf4', color: message.includes('Error') ? '#dc2626' : '#15803d', border: `1px solid ${message.includes('Error') ? '#fecaca' : '#bbf7d0'}` }}>
              {message}
            </div>
          )}

          <button onClick={handleSave} style={{ padding: '11px', background: TEAL, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: FONT }}>
            Save Settings
          </button>
        </div>
      </Card>

      {/* ── 4. Legal & Compliance ─────────────────────────────────────────────── */}
      <Card>
        <SectionHead
          label="Legal & Compliance"
          sub="HIPAA documentation and legal agreements for your clinic."
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[
            {
              title: 'Business Associate Agreement (BAA)',
              desc: 'HIPAA §164.504(e) — required for all covered entities using ClearPath PT. Covers all nine mandatory provisions.',
              href: '/baa',
              badge: 'HIPAA Required',
              badgeColor: '#dc2626',
              badgeBg: '#fef2f2',
              badgeBorder: '#fecaca',
            },
            {
              title: 'Privacy Policy',
              desc: 'How ClearPath PT collects, uses, and protects patient and clinic data under HIPAA and applicable law.',
              href: '/privacy',
              badge: 'HIPAA NPP',
              badgeColor: '#0369a1',
              badgeBg: '#f0f9ff',
              badgeBorder: '#bae6fd',
            },
            {
              title: 'Terms of Service',
              desc: 'The agreement governing your use of the ClearPath PT platform, including clinic and therapist obligations.',
              href: '/terms',
              badge: 'Legal',
              badgeColor: '#7c3aed',
              badgeBg: '#f5f3ff',
              badgeBorder: '#e9d5ff',
            },
          ].map(doc => (
            <a
              key={doc.href}
              href={doc.href}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '10px', transition: 'border-color 0.15s', cursor: 'pointer', gap: '12px' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = TEAL_MID}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>{doc.title}</span>
                  <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '99px', background: doc.badgeBg, color: doc.badgeColor, border: `1px solid ${doc.badgeBorder}`, whiteSpace: 'nowrap' }}>{doc.badge}</span>
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.5' }}>{doc.desc}</div>
              </div>
              <div style={{ fontSize: '16px', color: '#94a3b8', flexShrink: 0 }}>→</div>
            </a>
          ))}
        </div>

        <div style={{ marginTop: '14px', padding: '12px 16px', background: TEAL_LIGHT, border: `1px solid ${TEAL_MID}`, borderRadius: '10px', fontSize: '12px', color: TEAL_DARK, lineHeight: '1.6' }}>
          <strong>Note:</strong> Your BAA is executed when you register your clinic and is stored on file. If you need a signed copy for your records, contact <a href="mailto:ericmoorexc@gmail.com" style={{ color: TEAL_DARK, fontWeight: '600' }}>support</a>.
        </div>
      </Card>

    </div>
  )
}

export default Settings
